"use client";

import { MILESTONES } from "@/lib/wikipedia";
import { SpeechBubbleIcon } from "@/components/Icons";

interface ProgressBarProps {
  totalRead: number;
  milestonesAchieved: number[];
  onMilestoneClick: (milestone: typeof MILESTONES[number]) => void;
}

export function ProgressBar({
  totalRead,
  milestonesAchieved,
  onMilestoneClick,
}: ProgressBarProps) {
  const maxMilestone = MILESTONES[MILESTONES.length - 1].count;
  const progressPercent = Math.min((totalRead / maxMilestone) * 100, 100);

  return (
    <div className="w-full space-y-5">
      {/* Progress bar container */}
      <div 
        className="py-5 px-6 rounded-2xl border border-[#141beb]/15 shadow-md"
        style={{ background: 'linear-gradient(to bottom right, #e8efff, #f0f6ff, #e5f4ff)' }}
      >
        <div className="relative mb-8">
        {/* Progress track with glow effect */}
        <div className="h-3 rounded-full overflow-hidden shadow-inner relative border border-[#141beb]/20" style={{ backgroundColor: '#c7d7f5' }}>
          <div
            className="h-full bg-gradient-to-r from-[#141beb] to-[#02eec4] transition-all duration-500 ease-out progress-glow rounded-full"
            style={{ width: `${progressPercent}%` }}
          />
          {/* Animated particles on the progress bar */}
          {progressPercent > 0 && (
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full opacity-60 sparkle"
              style={{ left: `${Math.max(progressPercent - 5, 2)}%` }}
            />
          )}
        </div>

        {/* Milestone markers */}
        <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between pointer-events-none">
          {MILESTONES.map((milestone, index) => {
            const position = (milestone.count / maxMilestone) * 100;
            const isAchieved = milestonesAchieved.includes(milestone.id);
            const isNext = !isAchieved && 
              milestonesAchieved.length === milestone.id - 1;

            return (
              <div
                key={milestone.id}
                className="absolute pointer-events-auto"
                style={{ left: `${position}%`, transform: "translateX(-50%)" }}
              >
                <button
                  onClick={() => isAchieved && onMilestoneClick(milestone)}
                  disabled={!isAchieved}
                  className={`
                    relative w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm
                    transition-all duration-300 transform
                    ${isAchieved
                      ? "bg-gradient-to-br from-[#02eec4] to-[#141beb] text-white shadow-lg hover:scale-125 cursor-pointer icon-wiggle"
                      : isNext
                        ? "bg-[#f6fbff] border-2 border-[#02eec4] text-gray-400 float-animation pulse-ring"
                        : "bg-gray-100 border-2 border-gray-300 text-gray-300"
                    }
                  `}
                  style={{ animationDelay: `${index * 0.5}s` }}
                  title={isAchieved ? `${milestone.name} - Click to celebrate!` : `${milestone.count} articles to unlock`}
                >
                  <span className={`${isAchieved ? "" : "grayscale"} ${isAchieved ? "float-animation" : ""}`} style={{ animationDelay: `${index * 0.3}s` }}>
                    {milestone.icon}
                  </span>
                </button>
                <div className="text-[8px] sm:text-[10px] text-center mt-1 text-gray-500 font-medium">
                  {milestone.count}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      </div>

      {/* Current progress callout - styled box with speech bubble - separate from progress bar */}
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#fffde7] border border-[#f9e547]/50 rounded-xl shadow-sm">
          <SpeechBubbleIcon size={18} className="text-[#d4a900] flex-shrink-0" />
          <span className="text-xs sm:text-sm text-gray-700">
            {totalRead < MILESTONES[0].count ? (
              <>
                <span className="font-bold text-[#141beb]">{MILESTONES[0].count - totalRead}</span> more to unlock{" "}
                <span className="font-semibold">{MILESTONES[0].name}</span>!
              </>
            ) : totalRead >= maxMilestone ? (
              <span className="text-[#141beb] font-bold">
                🎉 You&apos;ve achieved all milestones! Legend! 🎉
              </span>
            ) : (
              <>
                <span className="font-bold text-[#141beb]">
                  {MILESTONES.find((m) => m.count > totalRead)!.count - totalRead}
                </span>{" "}
                more to unlock{" "}
                <span className="font-semibold">
                  {MILESTONES.find((m) => m.count > totalRead)!.name}
                </span>
                !
              </>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
